import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FastForwardFilled,
  PauseCircleFilled,
  PlayCircleFilled,
  FastBackwardFilled,
  StepForwardFilled,
  StepBackwardFilled,
  SwapOutlined,
  StopFilled,
  RetweetOutlined,
} from '@ant-design/icons';
import { useNuPlayerContext } from '../NuPlayerContext';
import { getPublicImagePath, useBreakpoints } from '@nutastic/nutastic-react-app';

export const Controls = ({ hideBackButton }) => {
  const {
    tracks,
    currentTrack,
    audioRef,
    setDuration,
    duration,
    setTimeProgress,
    progressBarRef,
    setTrackIndex,
    trackIndex,
    isPlaying,
    setIsPlaying,
    playerMode,
    PLAYER_MODES,
    onLoadBroadcast,
    playerConfigData
  } = useNuPlayerContext();
  const [isRepeat, setIsRepeat] = useState(false);
  const playAnimationRef = useRef(null);

  const updateProgress = useCallback(() => {
    if (audioRef.current && progressBarRef.current && duration) {
      const currentTime = audioRef.current.currentTime;
      setTimeProgress(currentTime);

      progressBarRef.current.value = currentTime.toString();
      progressBarRef.current.style.setProperty(
        '--range-progress',
        `${(currentTime / duration) * 100}%`
      );
    }
  }, [duration, setTimeProgress, audioRef, progressBarRef]);

  const startAnimation = useCallback(() => {
    if (audioRef.current && progressBarRef.current && duration) {
      const animate = () => {
        updateProgress();
        playAnimationRef.current = requestAnimationFrame(animate);
      };
      playAnimationRef.current = requestAnimationFrame(animate);
    }
  }, [updateProgress, duration, audioRef, progressBarRef]);

  useEffect(() => {
    if (isPlaying) {
      audioRef.current?.play();
      startAnimation();
    } else {
      audioRef.current?.pause();
      if (playAnimationRef.current !== null) {
        cancelAnimationFrame(playAnimationRef.current);
        playAnimationRef.current = null;
      }
      updateProgress(); // Ensure progress is updated immediately when paused
    }

    return () => {
      if (playAnimationRef.current !== null) {
        cancelAnimationFrame(playAnimationRef.current);
      }
    };
  }, [isPlaying, startAnimation, updateProgress, audioRef]);

  const updateMediaSession = () => {
    console.log('Player data session update', playerConfigData);
    console.log('Current track data session update', currentTrack);
    const { projectTitle } = playerConfigData;
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata(currentTrack ? {
        title: currentTrack.title,
        artist: currentTrack.author,
        album: `NuStation.net ${ projectTitle ? `| ${projectTitle}` : ''}`,
        artwork: [
          { src: currentTrack.thumbnail }
        ]
      } : null);
    }
  };

  const onLoadedMetadata = () => {
    updateMediaSession();
    const seconds = audioRef.current?.duration;
    if (seconds !== undefined) {
      setDuration(seconds);
      if (progressBarRef.current) {
        progressBarRef.current.max = seconds.toString();
      }
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime += 15;
      updateProgress();
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime -= 15;
      updateProgress();
    }
  };

  const handlePrevious = () => {
    if (audioRef.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      updateProgress();
    } else {
      const nextIndex = trackIndex === 0 ? 0 : trackIndex - 1;
      setTrackIndex(nextIndex);
    }
  };

  const handleNext = () => {
    const isAtEndOfPlaylist = trackIndex >= tracks.length - 1;
    const nextIndex = isAtEndOfPlaylist ? 0 : trackIndex + 1;
    if (isAtEndOfPlaylist && playerMode !== PLAYER_MODES.RADIO) {
      setIsPlaying(false);
    }
    setTrackIndex(nextIndex);
  };

  const handlePlay = () => {
    if(playerMode === PLAYER_MODES.RADIO) {
      onLoadBroadcast();
    } else {
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  //useEffect for when the track changes
  useEffect(() => {
    const currentAudioRef = audioRef.current;

    if (currentAudioRef) {
      currentAudioRef.onended = () => {
        if (isRepeat) currentAudioRef.play();
        else handleNext();
      };
      currentAudioRef.onpause = () => {
        if (currentAudioRef.currentTime > 1 && currentAudioRef.currentTime < (duration - 1)) {
          //if loop hack because browser keeps pausing when track ends and stopping it from continuing to play
          setIsPlaying(false);
        }
      }
      currentAudioRef.onplay = () => setIsPlaying(true);

      //Change mediaSession action handlers based on PLAYER_MODE
      if ('mediaSession' in navigator) {
        if (playerMode === PLAYER_MODES.RADIO) {
          navigator.mediaSession.setActionHandler('seekbackward', null);
          navigator.mediaSession.setActionHandler('seekforward', null);
          navigator.mediaSession.setActionHandler('nexttrack', null);
          navigator.mediaSession.setActionHandler('previoustrack', null);
          navigator.mediaSession.setActionHandler('play', () => handlePlay());
          navigator.mediaSession.setActionHandler('stop', () => handlePause());
          navigator.mediaSession.setActionHandler('pause', null);
          navigator.mediaSession.setActionHandler('seekto', null);
        } else {
          navigator.mediaSession.setActionHandler('seekbackward', null);
          navigator.mediaSession.setActionHandler('seekforward', null);
          navigator.mediaSession.setActionHandler('nexttrack', handleNext);
          navigator.mediaSession.setActionHandler('previoustrack', handlePrevious);
          navigator.mediaSession.setActionHandler('play', () => handlePlay());
          navigator.mediaSession.setActionHandler('pause', () => handlePause());
          navigator.mediaSession.setActionHandler('seekto', (details) => {
            currentAudioRef.currentTime = details.seekTime;
          });
        }
      }
    }

    return () => {
      if (currentAudioRef) {
        currentAudioRef.onended = null;
        currentAudioRef.onpause = null;
        currentAudioRef.onplay = null;
      }
    };
  }, [isRepeat, handleNext, audioRef, playerMode]);

  const pauseButton = playerMode === PLAYER_MODES.RADIO ? <StopFilled /> : <PauseCircleFilled />;

  return <div className="npControls">
    <audio src={currentTrack.src} ref={audioRef} onLoadedMetadata={onLoadedMetadata}/>
    {(!hideBackButton && playerMode !== PLAYER_MODES.RADIO) && <button onClick={handlePrevious}><StepBackwardFilled /></button> }
    <button onClick={isPlaying ? handlePause : handlePlay}>{ isPlaying ? <PauseCircleFilled /> : <PlayCircleFilled /> }</button>
    { (playerMode !== PLAYER_MODES.RADIO) && <button onClick={handleNext}><StepForwardFilled /></button> }
  </div>;
};
